import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import * as IssuesV2Store from '../../../../redux/issuesV2';
import AccordionBody from '../../../generic/Accordion/AccordionBody';
import useAccordion from '../../../generic/Accordion/useAccordion';
import Button from '../../../generic/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { Icon, icons } from '../../../generic/Icon';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';

import { FossabotExportTicketModal } from './FossabotExportTicketModal';
import { FossabotFixOptionCard, FossabotSectionLabel } from './FossabotFixOptionCard';
import { FossabotTicketCreated } from './FossabotTicketCreated';
import { useFossabotFixOptions } from './useFossabotFixOptions';

import styles from './FossabotOtherFixesBox.module.less';

/**
 * The collapsible "Other fixes available" card shown below the FossabotFixBox
 * when the org has the fossabot GitHub app installed. Lists every upgrade under
 * CREATE TICKET; Create opens the Jira export modal seeded with the selected
 * version. Once the issue has a ticket, the body shows the TICKET CREATED link
 * instead.
 */
type Props = {
  /** Start expanded; outside project scope this card is the only fix path. */
  isInitiallyOpen?: boolean;
};

export const FossabotOtherFixesBox: VoidFunctionComponent<Props> = ({ isInitiallyOpen = false }) => {
  const { options, cve } = useFossabotFixOptions();
  const isTicketed = useSelector(IssuesV2Store.selectors.getIsIssueTicketed);
  const ticket = useSelector(IssuesV2Store.selectors.getFirstJiraTicket);
  const { fossabotExportModal } = useIssueDetailsModals();
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const { isOpen, targetProps, bodyProps } = useAccordion({ id: 'fossabot-other-fixes', isInitiallyOpen });

  if (options.length === 0) {
    return null;
  }

  const existingTicket = isTicketed ? ticket : null;

  return (
    <div className={styles.card} data-testid="fossabot-other-fixes-box">
      <button
        id={targetProps.id}
        type="button"
        className={styles.target}
        onClick={targetProps.onClick}
        aria-controls={targetProps['aria-controls']}
        aria-expanded={targetProps['aria-expanded']}
      >
        Other fixes available
        <Icon icon={isOpen ? icons.angleUp : icons.angleDown} />
      </button>

      <AccordionBody
        isOpen={isOpen}
        id={bodyProps.id}
        role={bodyProps.role}
        aria-labelledby={bodyProps['aria-labelledby']}
        className={styles.body}
      >
        {/*
          These are siblings rather than one wrapped node on purpose: AccordionBody
          clones a lone child element to hand it `isOpen`, and React rejects that
          prop on a plain DOM node.
        */}
        {existingTicket && <FossabotTicketCreated ticket={existingTicket} />}
        {!existingTicket && (
          <div className={styles.group}>
            <FossabotSectionLabel>CREATE TICKET</FossabotSectionLabel>
            {options.map((option) => (
              <FossabotFixOptionCard
                key={option.version}
                option={option}
                cve={cve}
                value={option.version}
                isSelected={selectedVersion === option.version}
                onSelect={() => setSelectedVersion(option.version)}
              />
            ))}
          </div>
        )}
        {!existingTicket && (
          <div className={styles.actions}>
            <Button
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              isDisabled={selectedVersion === null}
              onClick={fossabotExportModal.onOpen}
            >
              Create Ticket
            </Button>
          </div>
        )}
      </AccordionBody>

      <FossabotExportTicketModal remediationVersion={selectedVersion ?? undefined} />
    </div>
  );
};
